import { Download } from "@tamagui/lucide-icons";
import { BlurView } from "expo-blur";
import { useEffect, useRef } from "react";
import { ScrollView, XStack, YStack } from "tamagui";

import { useCreateSoapNotePdfLink } from "@/api/generated";
import {
  Button,
  CloseButton,
  Heading,
  Link,
  List,
  Paragraph,
  Section,
  Separator,
  Text,
} from "@/components";
import { shortBrandName } from "@/config/brand";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import useRedirectUrl from "@/hooks/useRedirectUrl";
import { downloadMessageFile } from "@/utils/downloadMessageFile";

type SOAPNoteDisplayProps = {
  messageId?: string;
  onClose: () => void;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string[];
};

const SOAPNoteDisplay = ({
  messageId,
  subjective,
  objective,
  assessment,
  plan,
  onClose,
}: SOAPNoteDisplayProps) => {
  const conversationId = useCurrentConversationId();
  const convoIdRef = useRef(conversationId);
  const isSignedInStable = useIsSignedInStable();
  const redirectUrl = useRedirectUrl();

  const signInHref = {
    pathname: "/sign-in",
    params: { redirectUrl, currentConversationId: conversationId },
  };

  const { refetch, isFetching } = useCreateSoapNotePdfLink(messageId!, {
    query: {
      enabled: false,
      gcTime: 0,
    },
  });

  useEffect(() => {
    if (conversationId !== convoIdRef.current) {
      onClose();
    }
    convoIdRef.current = conversationId;
  }, [conversationId, onClose]);

  let downloadOrSignup;
  if (isSignedInStable) {
    downloadOrSignup = (
      <Button
        mode="tertiaryWithIcon"
        icon={Download}
        onPress={() =>
          downloadMessageFile({
            messageId: messageId!,
            path: "soap-note-pdf",
            fetchKey: refetch,
          })
        }
        disabled={isFetching}
      >
        Download
      </Button>
    );
  } else {
    downloadOrSignup = (
      <Text size="$sm" marginVertical="$150">
        <Link
          size="$sm"
          href={signInHref}
          dataTrackingId="health-summary-create-account"
        >
          Create an account
        </Link>
        &nbsp;or&nbsp;
        <Link
          size="$sm"
          href={signInHref}
          dataTrackingId="health-summary-log-in"
        >
          log in
        </Link>
        &nbsp;to download
      </Text>
    );
  }

  return (
    <YStack height="100%" borderRadius="$150" backgroundColor="$fillModal">
      <XStack
        justifyContent="flex-end"
        paddingTop="$200"
        paddingHorizontal="$200"
      >
        <CloseButton onClose={onClose} dataTrackingId="soap-note-close" />
      </XStack>
      <Section paddingHorizontal="$200" paddingTop="$100">
        <Heading size="$lg">Health summary</Heading>
        <Paragraph>
          Bring this health summary to your visit with your clinician.{" "}
          {shortBrandName} is not a replacement for a clinician.
        </Paragraph>
        <Separator borderColor="$fillNeutral" marginBottom="$150" />
      </Section>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          marginBottom: "$800",
          paddingHorizontal: "$200",
        }}
      >
        <Section>
          <Paragraph>
            <Text bold>Subjective:</Text>&nbsp;{subjective}
          </Paragraph>
          <Paragraph>
            <Text bold>Objective:</Text>&nbsp;{objective}
          </Paragraph>
          <Paragraph>
            <Text bold>Assessment:</Text>&nbsp;{assessment}
          </Paragraph>
          <Paragraph>
            <Text bold>Plan:</Text>
          </Paragraph>
          <List
            listItems={plan.map((item) => ({ label: item })) || []}
            color="$textPrimary"
          />
        </Section>
      </ScrollView>
      <BlurView
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          borderBottomRightRadius: 12,
          borderBottomLeftRadius: 12,
        }}
      >
        <XStack paddingVertical="$100" marginHorizontal="auto">
          {downloadOrSignup}
        </XStack>
      </BlurView>
    </YStack>
  );
};

export default SOAPNoteDisplay;
